import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
} from "@/components/ui";
import {
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Download,
  Calendar,
  Banknote,
  Paperclip,
  FileText,
} from "lucide-react";
import financeService from "../../services/financeService";
import { projectService } from "../../services/projectService";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-hot-toast";
import BudgetSelectorWithBalance from "../../components/common/BudgetSelectorWithBalance";

const SUBSIDIARIES = [
  { value: "puffin_group", label: "Puffin Group Ltd" },
  { value: "buildwise", label: "Buildwise Construction Ltd" },
  { value: "laser_shield", label: "Laser Shield Ltd." },
  { value: "maz_logistics", label: "Maz Logistics Ltd." },
  { value: "puffin_energy", label: "Puffin Energy Ltd." },
  { value: "swingtide", label: "Swingtide Consulting Ltd" },
  { value: "puffin_agro", label: "Puffin Agro Allied" },
];

const TransactionsManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [confirmPaymentData, setConfirmPaymentData] = useState({
    payment_reference: "",
    comments: "",
  });

  // Form state
  const [transactionForm, setTransactionForm] = useState({
    subsidiary: "",
    project_id: "",
    budget_id: "",
    type: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    debit_account_id: "",
    credit_account_id: "",
    amount: "",
    reference_type: "",
    reference_id: "",
    reference_number: "",
    payment_method: "transfer", // transfer or cash
    beneficiary_name: "",
    beneficiary_bank: "",
    beneficiary_account: "",
    attachment: null, // New field for file upload
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    status: "",
    subsidiary: "",
    date_from: "", // No default date filter
    date_to: "", // No default date filter
    account_id: "",
    page: 1,
    limit: 10,
  });

  const [pagination, setPagination] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [accountsResponse, projectsResponse] = await Promise.all([
        financeService.getAccounts(),
        projectService.getProjects({ limit: 100 }),
      ]);
      const accountsData = accountsResponse?.data.data?.accounts || [];
      setAccounts(accountsData);
      setProjects(projectsResponse?.data?.data?.projects || []);
      if (accountsData.length === 0) {
        toast.error("No accounts found. Please create accounts first.");
      }
    } catch (error) {
      toast.error(
        `Failed to load data: ${error.response?.data?.message || error.message}`,
      );
      console.error("Load initial data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);

      // Clean up filters - remove empty strings to avoid validation errors
      const cleanedFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value !== "" && value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        },
        {},
      );

      const response = await financeService.getTransactions(cleanedFilters);
      setTransactions(response?.data?.data?.transactions || []);
      setPagination(response?.data?.data?.pagination || {});
    } catch (error) {
      toast.error("Failed to load transactions");
      console.error("Load transactions error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransaction = () => {
    setSelectedTransaction(null);
    setTransactionForm({
      subsidiary: "",
      project_id: "",
      budget_id: "",
      type: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
      debit_account_id: "",
      credit_account_id: "",
      amount: "",
      reference_type: "",
      reference_id: "",
      reference_number: "",
      payment_method: "transfer",
      beneficiary_name: "",
      beneficiary_bank: "",
      beneficiary_account: "",
      attachment: null,
    });
    setFormErrors({});
    setShowTransactionDialog(true);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowViewDialog(true);
  };

  const handleSubmitTransaction = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setFormErrors({});

      // Validate required fields
      const requiredFields = [
        "type",
        "description",
        "debit_account_id",
        "credit_account_id",
        "amount",
      ];
      const missingFields = requiredFields.filter(
        (field) => !transactionForm[field],
      );

      if (missingFields.length > 0) {
        toast.error(`Missing required fields: ${missingFields.join(", ")}`);
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      
      // Add all form fields
      formData.append("type", transactionForm.type);
      formData.append("date", transactionForm.date);
      formData.append("description", transactionForm.description);
      formData.append("debit_account_id", parseInt(transactionForm.debit_account_id));
      formData.append("credit_account_id", parseInt(transactionForm.credit_account_id));
      formData.append("amount", parseFloat(transactionForm.amount));
      formData.append("payment_method", transactionForm.payment_method || "transfer");
      formData.append("status", "pending_gmd_approval");

      // Add beneficiary details if payment method is transfer
      if (transactionForm.payment_method === "transfer") {
        formData.append("beneficiary_name", transactionForm.beneficiary_name);
        formData.append("beneficiary_bank", transactionForm.beneficiary_bank);
        formData.append("beneficiary_account", transactionForm.beneficiary_account);
      }

      // Add optional fields if provided
      if (transactionForm.subsidiary) {
        formData.append("subsidiary", transactionForm.subsidiary);
      }
      if (transactionForm.project_id) {
        formData.append("project_id", parseInt(transactionForm.project_id));
      }
      if (transactionForm.budget_id) {
        formData.append("budget_id", parseInt(transactionForm.budget_id));
      }
      if (transactionForm.reference_type) {
        formData.append("reference_type", transactionForm.reference_type);
      }
      
      // Add attachment if provided
      if (transactionForm.attachment) {
        formData.append("attachment", transactionForm.attachment);
      }

      console.log("Transaction with attachment being sent");
      await financeService.createTransaction(formData);
      toast.success("Transaction created and sent to GMD for approval");

      setShowTransactionDialog(false);
      loadTransactions();
    } catch (error) {
      console.error("Full error object:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);

      const errorMessage =
        error.response?.data?.message || "Failed to create transaction";
      toast.error(errorMessage);

      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTransaction = async (transactionId) => {
    if (!confirm("Are you sure you want to approve this transaction?")) {
      return;
    }

    try {
      setLoading(true);
      await financeService.approveTransaction(transactionId);
      toast.success("Transaction approved successfully");
      loadTransactions();
    } catch (error) {
      toast.error("Failed to approve transaction");
      console.error("Approve transaction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (
      !confirm(
        "Are you sure you want to delete this transaction? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await financeService.deleteTransaction(transactionId);
      toast.success("Transaction deleted successfully");
      loadTransactions();
    } catch (error) {
      toast.error("Failed to delete transaction");
      console.error("Delete transaction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock },
      pending_gmd_approval: {
        color: "bg-orange-100 text-orange-800",
        icon: Clock,
      },
      approved: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      rejected: { color: "bg-red-100 text-red-800", icon: XCircle },
      posted: { color: "bg-blue-100 text-blue-800", icon: CheckCircle },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status === "pending_gmd_approval"
          ? "Pending GMD"
          : status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const transactionTypes = [
    { value: "journal", label: "Journal Entry" },
    { value: "payment", label: "Payment" },
    { value: "receipt", label: "Receipt" },
    { value: "transfer", label: "Transfer" },
    { value: "cash", label: "Cash Payment" },
    { value: "adjustment", label: "Adjustment" },
  ];

  const referenceTypes = [
    { value: "invoice", label: "Invoice" },
    { value: "bill", label: "Bill" },
    { value: "payroll", label: "Payroll" },
    { value: "expense", label: "Expense" },
    { value: "project", label: "Project" },
    { value: "asset", label: "Asset" },
    { value: "manual", label: "Other (Manual)" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Transactions</h1>
          <p className="text-gray-600">
            Manage financial transactions and journal entries
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button
            onClick={handleCreateTransaction}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Transaction
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Search transactions..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                      page: 1,
                    }))
                  }
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="type">Type</Label>
              <Select
                value={filters.type}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, type: value, page: 1 }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Types</SelectItem>
                  {transactionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, status: value, page: 1 }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="pending_gmd_approval">
                    Pending GMD
                  </SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="subsidiary">Subsidiary</Label>
              <Select
                value={filters.subsidiary}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    subsidiary: value,
                    page: 1,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Subsidiaries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Subsidiaries</SelectItem>
                  {SUBSIDIARIES.map((sub) => (
                    <SelectItem key={sub.value} value={sub.value}>
                      {sub.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="date_from">From Date</Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    date_from: e.target.value,
                    page: 1,
                  }))
                }
              />
            </div>

            <div>
              <Label htmlFor="date_to">To Date</Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    date_to: e.target.value,
                    page: 1,
                  }))
                }
              />
            </div>

            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  setFilters({
                    search: "",
                    type: "",
                    status: "",
                    subsidiary: "",
                    date_from: "",
                    date_to: "",
                    account_id: "",
                    page: 1,
                    limit: 10,
                  })
                }
                className="flex items-center gap-2"
              >
                Clear Filters
              </Button>
              <Button
                variant="outline"
                onClick={loadTransactions}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transactions ({pagination.totalItems || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {transactions.map((transaction) => (
                  <Card key={transaction.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-gray-500">Transaction</p>
                            <p className="font-mono text-sm font-semibold">
                              {transaction.transaction_number}
                            </p>
                          </div>
                          {getStatusBadge(transaction.status)}
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Date</p>
                          <p className="text-sm">{new Date(transaction.date).toLocaleDateString()}</p>
                        </div>

                        {transaction.subsidiary && (
                          <div>
                            <p className="text-xs text-gray-500">Subsidiary</p>
                            <p className="text-sm">
                              {SUBSIDIARIES.find((s) => s.value === transaction.subsidiary)?.label || "-"}
                            </p>
                          </div>
                        )}

                        <div>
                          <p className="text-xs text-gray-500">Description</p>
                          <p className="text-sm font-medium line-clamp-2">{transaction.description}</p>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize text-xs">
                            {transaction.type}
                          </Badge>
                          {transaction.reference_number && (
                            <span className="text-xs text-gray-500">
                              Ref: {transaction.reference_number}
                            </span>
                          )}
                        </div>

                        <div className="pt-2 border-t flex justify-between items-center">
                          <span className="text-xs text-gray-500">Amount</span>
                          <span className="text-lg font-bold text-green-600">
                            {financeService.formatCurrency(transaction.amount)}
                          </span>
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewTransaction(transaction)}
                            className="flex-1"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>

                          {transaction.status === "approved" && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setConfirmPaymentData({
                                  payment_reference: "",
                                  comments: "",
                                });
                                setShowConfirmDialog(true);
                              }}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Confirm
                            </Button>
                          )}

                          {(transaction.status === "pending_gmd_approval" ||
                            transaction.status === "pending") && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              className="flex-1 text-red-600 hover:text-red-700"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    Showing{" "}
                    {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}{" "}
                    to{" "}
                    {Math.min(
                      pagination.currentPage * pagination.itemsPerPage,
                      pagination.totalItems,
                    )}{" "}
                    of {pagination.totalItems} transactions
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.currentPage <= 1}
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, page: prev.page - 1 }))
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.currentPage >= pagination.totalPages}
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Transaction Dialog */}
      <Dialog
        open={showTransactionDialog}
        onOpenChange={setShowTransactionDialog}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Create New Transaction
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitTransaction} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="subsidiary">Subsidiary *</Label>
                <Select
                  value={transactionForm.subsidiary}
                  onValueChange={(value) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      subsidiary: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subsidiary" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBSIDIARIES.map((sub) => (
                      <SelectItem key={sub.value} value={sub.value}>
                        {sub.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.subsidiary && (
                  <p className="text-sm text-red-600 mt-1">
                    {formErrors.subsidiary}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="project_id">Project</Label>
                <Select
                  value={transactionForm.project_id}
                  onValueChange={(value) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      project_id: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {projects.map((project) => (
                      <SelectItem
                        key={project.id}
                        value={project.id.toString()}
                      >
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <BudgetSelectorWithBalance
                value={transactionForm.budget_id}
                onChange={(value) =>
                  setTransactionForm((prev) => ({
                    ...prev,
                    budget_id: value,
                  }))
                }
                label="Budget"
                required={false}
                showBalance={true}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Transaction Type *</Label>
                <Select
                  value={transactionForm.type}
                  onValueChange={(value) =>
                    setTransactionForm((prev) => ({ ...prev, type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {transactionTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.type && (
                  <p className="text-sm text-red-600 mt-1">{formErrors.type}</p>
                )}
              </div>

              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                  required
                />
                {formErrors.date && (
                  <p className="text-sm text-red-600 mt-1">{formErrors.date}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={transactionForm.description}
                onChange={(e) =>
                  setTransactionForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Transaction description"
                rows={3}
                required
              />
              {formErrors.description && (
                <p className="text-sm text-red-600 mt-1">
                  {formErrors.description}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="debit_account_id">Debit Account *</Label>
                <Select
                  value={transactionForm.debit_account_id}
                  onValueChange={(value) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      debit_account_id: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        accounts.length > 0
                          ? "Select debit account"
                          : "Loading accounts..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.length > 0 ? (
                      accounts.map((account) => (
                        <SelectItem
                          key={account.id}
                          value={account.id.toString()}
                        >
                          {account.code} - {account.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>
                        No accounts available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {formErrors.debit_account_id && (
                  <p className="text-sm text-red-600 mt-1">
                    {formErrors.debit_account_id}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="credit_account_id">Credit Account *</Label>
                <Select
                  value={transactionForm.credit_account_id}
                  onValueChange={(value) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      credit_account_id: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        accounts.length > 0
                          ? "Select credit account"
                          : "Loading accounts..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.length > 0 ? (
                      accounts.map((account) => (
                        <SelectItem
                          key={account.id}
                          value={account.id.toString()}
                        >
                          {account.code} - {account.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>
                        No accounts available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {formErrors.credit_account_id && (
                  <p className="text-sm text-red-600 mt-1">
                    {formErrors.credit_account_id}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={transactionForm.amount}
                onChange={(e) =>
                  setTransactionForm((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                placeholder="0.00"
                required
              />
              {formErrors.amount && (
                <p className="text-sm text-red-600 mt-1">{formErrors.amount}</p>
              )}
            </div>

            {/* Payment Method Selection */}
            <div>
              <Label htmlFor="payment_method">Payment Method *</Label>
              <Select
                value={transactionForm.payment_method}
                onValueChange={(value) =>
                  setTransactionForm((prev) => ({
                    ...prev,
                    payment_method: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Beneficiary Details - Only show for Transfer */}
            {transactionForm.payment_method === "transfer" && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900">
                  Beneficiary Details
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="beneficiary_name">Beneficiary Name *</Label>
                    <Input
                      id="beneficiary_name"
                      value={transactionForm.beneficiary_name}
                      onChange={(e) =>
                        setTransactionForm((prev) => ({
                          ...prev,
                          beneficiary_name: e.target.value,
                        }))
                      }
                      placeholder="Enter beneficiary name"
                      required={transactionForm.payment_method === "transfer"}
                    />
                  </div>
                  <div>
                    <Label htmlFor="beneficiary_bank">Bank Name *</Label>
                    <Input
                      id="beneficiary_bank"
                      value={transactionForm.beneficiary_bank}
                      onChange={(e) =>
                        setTransactionForm((prev) => ({
                          ...prev,
                          beneficiary_bank: e.target.value,
                        }))
                      }
                      placeholder="Enter bank name"
                      required={transactionForm.payment_method === "transfer"}
                    />
                  </div>
                  <div>
                    <Label htmlFor="beneficiary_account">
                      Account Number *
                    </Label>
                    <Input
                      id="beneficiary_account"
                      value={transactionForm.beneficiary_account}
                      onChange={(e) =>
                        setTransactionForm((prev) => ({
                          ...prev,
                          beneficiary_account: e.target.value,
                        }))
                      }
                      placeholder="Enter account number"
                      required={transactionForm.payment_method === "transfer"}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Cash Payment Notice */}
            {transactionForm.payment_method === "cash" && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <Banknote className="h-5 w-5 text-green-600" />
                  <p className="text-green-800 font-medium">
                    Cash Payment - No beneficiary details required
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reference_type">Reference Type</Label>
                <Select
                  value={transactionForm.reference_type}
                  onValueChange={(value) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      reference_type: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reference type" />
                  </SelectTrigger>
                  <SelectContent>
                    {referenceTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reference_number">Reference Number</Label>
                <Input
                  id="reference_number"
                  value="Auto-generated"
                  disabled
                  className="bg-gray-100"
                />
              </div>
            </div>

            {/* Attachment Upload */}
            <div>
              <Label htmlFor="attachment">Attachment (Optional)</Label>
              <div className="mt-1">
                <Input
                  id="attachment"
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Check file size (max 10MB)
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error("File size must be less than 10MB");
                        e.target.value = "";
                        return;
                      }
                      setTransactionForm((prev) => ({
                        ...prev,
                        attachment: file,
                      }));
                    }
                  }}
                  className="cursor-pointer"
                />
              </div>
              {transactionForm.attachment && (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                  <Paperclip className="h-4 w-4" />
                  <span>{transactionForm.attachment.name}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setTransactionForm((prev) => ({
                        ...prev,
                        attachment: null,
                      }))
                    }
                    className="text-red-600 hover:text-red-700"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Upload supporting documents (PDF, DOC, Images, Excel - Max 10MB)
              </p>
            </div>

            {/* Form Errors */}
            {Object.keys(formErrors).length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Please fix the errors above and try again.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTransactionDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Transaction"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Transaction Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Subsidiary</Label>
                  <p>
                    {SUBSIDIARIES.find(
                      (s) => s.value === selectedTransaction.subsidiary,
                    )?.label || "-"}
                  </p>
                </div>
                <div>
                  <Label>Project</Label>
                  <p>
                    {projects.find(
                      (p) => p.id == selectedTransaction.project_id,
                    )?.name || "-"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Transaction Number</Label>
                  <p className="font-mono text-sm">
                    {selectedTransaction.transaction_number}
                  </p>
                </div>
                <div>
                  <Label>Date</Label>
                  <p>
                    {new Date(selectedTransaction.date).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <p>{selectedTransaction.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Badge variant="outline" className="capitalize">
                    {selectedTransaction.type}
                  </Badge>
                </div>
                <div>
                  <Label>Status</Label>
                  {getStatusBadge(selectedTransaction.status)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Debit Account</Label>
                  <p>
                    {selectedTransaction.DebitAccount?.code} -{" "}
                    {selectedTransaction.DebitAccount?.name}
                  </p>
                </div>
                <div>
                  <Label>Credit Account</Label>
                  <p>
                    {selectedTransaction.CreditAccount?.code} -{" "}
                    {selectedTransaction.CreditAccount?.name}
                  </p>
                </div>
              </div>

              <div>
                <Label>Amount</Label>
                <p className="text-2xl font-bold">
                  {financeService.formatCurrency(selectedTransaction.amount)}
                </p>
              </div>

              {selectedTransaction.reference_number && (
                <div>
                  <Label>Reference</Label>
                  <p>
                    {selectedTransaction.reference_type}:{" "}
                    {selectedTransaction.reference_number}
                  </p>
                </div>
              )}

              {selectedTransaction.attachment_url && (
                <div>
                  <Label>Attachment</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <a
                      href={selectedTransaction.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                    >
                      View Attachment
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              )}

              {selectedTransaction.CreatedBy && (
                <div>
                  <Label>Created By</Label>
                  <p>Finance Department</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Payment Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-500">Transaction</p>
                        <p className="font-mono font-semibold">
                          {selectedTransaction.transaction_number}
                        </p>
                      </div>
                      {getStatusBadge(selectedTransaction.status)}
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">Description</p>
                      <p className="font-medium">{selectedTransaction.description}</p>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-sm text-gray-500">Amount</span>
                      <span className="text-2xl font-bold text-green-600">
                        {financeService.formatCurrency(selectedTransaction.amount)}
                      </span>
                    </div>

                    {selectedTransaction.beneficiary_name && (
                      <div className="pt-2 border-t">
                        <p className="text-sm text-gray-500">Beneficiary</p>
                        <p className="font-medium">{selectedTransaction.beneficiary_name}</p>
                        {selectedTransaction.beneficiary_bank && (
                          <p className="text-sm text-gray-600">
                            {selectedTransaction.beneficiary_bank} - {selectedTransaction.beneficiary_account}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div>
                <Label htmlFor="payment_reference">
                  Payment Reference Number *
                </Label>
                <Input
                  id="payment_reference"
                  value={confirmPaymentData.payment_reference}
                  onChange={(e) =>
                    setConfirmPaymentData((prev) => ({
                      ...prev,
                      payment_reference: e.target.value,
                    }))
                  }
                  placeholder="Enter payment reference"
                  required
                />
              </div>

              <div>
                <Label htmlFor="comments">Comments</Label>
                <Textarea
                  id="comments"
                  value={confirmPaymentData.comments}
                  onChange={(e) =>
                    setConfirmPaymentData((prev) => ({
                      ...prev,
                      comments: e.target.value,
                    }))
                  }
                  placeholder="Optional comments"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setSelectedTransaction(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!confirmPaymentData.payment_reference.trim()) {
                      toast.error("Payment reference is required");
                      return;
                    }

                    try {
                      setLoading(true);
                      await financeService.updateTransaction(
                        selectedTransaction.id,
                        {
                          status: "posted",
                          payment_reference: confirmPaymentData.payment_reference,
                          payment_confirmation_comments: confirmPaymentData.comments,
                        }
                      );
                      toast.success("Payment confirmed and posted to ledger");
                      setShowConfirmDialog(false);
                      setSelectedTransaction(null);
                      loadTransactions();
                    } catch (error) {
                      toast.error("Failed to confirm payment");
                      console.error("Confirm payment error:", error);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={
                    loading || !confirmPaymentData.payment_reference.trim()
                  }
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? "Confirming..." : "Confirm Payment"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionsManagement;
